import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

interface MetricCardProps {
  title: string
  value: string | number | undefined
  icon?: LucideIcon
  isLoading?: boolean
  onClick?: () => void
  className?: string
  'data-testid'?: string
}

export default function MetricCard({
  title,
  value,
  icon: Icon,
  isLoading = false,
  onClick,
  className,
  'data-testid': testId,
}: MetricCardProps) {
  const content = (
    <Card
      className={cn(
        'transition-all duration-200',
        onClick && 'cursor-pointer hover:scale-[1.02]',
        className
      )}
      style={{
        backgroundColor: 'var(--card-bg)',
        borderColor: 'var(--border)',
        boxShadow: 'var(--shadow-card)',
        borderRadius: 'var(--radius-lg)',
      }}
      onClick={onClick}
      data-testid={testId}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.boxShadow = 'var(--shadow-lg)'
        }
      }}
      onMouseLeave={(e) => {
        if (onClick) {
          e.currentTarget.style.boxShadow = 'var(--shadow-card)'
        }
      }}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle 
          className="text-sm font-medium"
          style={{
            color: 'var(--text-secondary)',
            fontFamily: 'var(--font-body)',
          }}
        >
          {title}
        </CardTitle>
        {Icon && (
          <Icon 
            className="h-4 w-4" 
            style={{
              color: 'var(--accent-gold)',
            }}
            aria-hidden="true" 
          />
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-8 w-24" data-testid={`${testId}-skeleton`} />
        ) : (
          <div 
            className="text-2xl font-bold"
            style={{
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-heading)',
            }}
            data-testid={`${testId}-value`}
          >
            {value ?? '—'}
          </div>
        )}
      </CardContent>
    </Card>
  )

  return content
}

